import {Model} from 'MWC';
import RealInterestRate from '../helper/realInterestRate';

const xirr = require('xirr');

const oneTimeCommission = 'one_time_commission',
    monthCommission = 'month_commission',
    insuranceRate = 'insurance_rate',
    timeUnit = 'time_unit',
    shopCreditTerm = 'shopCreditTerm',
    bankCreditTerm = 'bankCreditTerm',
    hundredPercent = 100,
    daysInTheYear = 365,
    bankDays = 360;

export default class extends Model {
    static fields;

    constructor(data, type) {
        super();
        this.selectedType = type;
        this.creditSum = data.min;
        this.requiredPayment = 0;
        this.overpayment = 0;
        this.creditCost = 0;
        this.realInterestRate = 0;
        this.realInterestRateList = [];
        this.shopCreditTerm = data.terms[0];
        this.bankCreditTerm = data.terms[0];
        this.bankMonthCommission = 0;
        this._initFields(data);
    }

    _initFields(data) {
        this.fields = {
            ...data
        };
    }

    changeField(name, value) {
        this[name] = value;

        if (name === shopCreditTerm) {
            this.bankCreditTerm = value;
        }

        if (name === bankCreditTerm && this.getCreditTerm() > +value) {
            this.shopCreditTerm = value;
        }

        this.calculateIndicators();
    }

    getCreditSum() {
        return +this.creditSum;
    }

    getOneTimeCommission() {
        return +this.fields[oneTimeCommission];
    }

    getMonthCommission() {
        return this.fields[monthCommission];
    }

    setBankMonthCommission(value) {
        this.bankMonthCommission = value;
    }

    getBankMonthCommission() {
        const bankTerm = this.getBankCreditTerm(),
            shopTerm = this.getCreditTerm();

        if (bankTerm === shopTerm) {
            return this.getMonthCommission();
        }

        if (!Array.isArray(this.bankMonthCommission)) {
            console.error(
                '[PayByParts] bankMonthCommission is not an array',
                this.bankMonthCommission
            );
            return this.getMonthCommission();
        }

        const entry = this.bankMonthCommission.find(
            item => item.term_store === shopTerm && item.term_client === bankTerm
        );

        if (!entry) {
            console.error(
                `[PayByParts] Commission not found for term_store=${shopTerm}, term_client=${bankTerm}`
            );
            return this.getMonthCommission();
        }

        return entry.komis;
    }

    checkInsurance() {
        return this.insured;
    }

    switchInsurance() {
        this.insured = !this.insured;
        this.calculateIndicators();
    }

    getBasicRate() {
        return this.fields.rate;
    }

    getInsuranceRate() {
        return this.checkInsurance() ? this.fields[insuranceRate] : 0;
    }

    getCurrentRate() {
        return +this.fields.rate * this.getCreditTerm();
    }

    getTimeUnit() {
        return this.fields[timeUnit];
    }

    getCreditTerm() {
        return +this.shopCreditTerm;
    }

    getBankCreditTerm() {
        return +this.bankCreditTerm;
    }

    getMinSumEdge() {
        return this.fields.min;
    }

    getFixAmounts() {
        return this.fields.fix_amounts;
    }

    getMaxSumEdge() {
        return this.fields.max;
    }

    getSumStep() {
        return this.fields.step;
    }

    getTerms() {
        return this.fields.terms;
    }

    getMinTermEdge() {
        return this.fields.terms[0];
    }

    getMaxTermEdge() {
        return this.fields.terms[this.fields.terms.length - 1];
    }

    getTermStep() {
        return 1;
    }

    getRequiredPayment() {
        return this.requiredPayment;
    }

    setRequiredPayment(value) {
        this.requiredPayment = value;
    }

    setSumWithOutFirstPayment(value) {
        this.sumWithOutFirstPayment = value;
    }

    setRealRateList(value) {
        this.realInterestRateList = value;
    }

    getOverpayment() {
        return this.overpayment;
    }

    setOverpayment(value) {
        this.overpayment = value;
    }

    getCreditCost() {
        return this.creditCost;
    }

    setCreditCost(value) {
        this.creditCost = value;
    }

    calculateIndicators() {
        const purchaseSum = this.getCreditSum();
        const numPayments = parseFloat(this.bankCreditTerm);
        const shopCreditTerm = parseFloat(this.shopCreditTerm);

        const monthCommissionRate = this.getBankMonthCommission() / hundredPercent;
        const annualRate = this.getBasicRate() / hundredPercent;

        // monthly rate adjusted for 365/360 day convention
        const monthlyRate = (annualRate * daysInTheYear) / bankDays / 12;

        // shared denominator used in both overpayment parts
        const overpaymentDenom =
            numPayments +
            (numPayments - 1) * monthCommissionRate +
            (monthlyRate * (numPayments - 2)) / 2;

        // amount financed after subtracting the store-subsidised portion
        const financedSum =
            purchaseSum -
            (purchaseSum * (1 + (numPayments - 1) * monthCommissionRate)) /
                (numPayments + (numPayments - 1) * monthCommissionRate);

        const annuityDenom = 1 - Math.pow(1 + monthlyRate, -(numPayments - 1));
        // zero-rate fallback: annuity formula degenerates when monthlyRate ≈ 0
        const annuityFactor =
            Math.abs(annuityDenom) < Number.EPSILON
                ? 1 / (numPayments - 1)
                : monthlyRate / annuityDenom;

        const requiredPayment = this.round(
            financedSum * annuityFactor + financedSum * monthCommissionRate
        );

        let overpayment = 0;

        if (!this.checkEqualTerms()) {
            if (Math.abs(overpaymentDenom) < Number.EPSILON) {
                console.error(
                    '[PayByParts] overpaymentDenom is zero, cannot calculate overpayment'
                );
            } else {
                const firstPayment =
                    (purchaseSum *
                        (1 +
                            (numPayments - 1) * monthCommissionRate +
                            (monthlyRate * (numPayments - 2)) / 2)) /
                    overpaymentDenom;
                const monthlyPart =
                    ((purchaseSum * (numPayments - 1)) / overpaymentDenom) *
                    (annuityFactor + monthCommissionRate);
                overpayment = this.round(
                    firstPayment + monthlyPart * (numPayments - 1) - purchaseSum
                );
            }
        }

        const creditCost = this.round(purchaseSum + overpayment);

        this.fullCreditSum = creditCost;
        this.setRequiredPayment(requiredPayment);
        this.setSumWithOutFirstPayment(financedSum);
        this.setCreditCost(creditCost);
        this.setOverpayment(overpayment);

        this.setRealInterestRate(shopCreditTerm, numPayments);
    }

    round(value) {
        return Math.round(value * 100) / 100;
    }

    addMonths(numOfMonths, date = new Date()) {
        date.setMonth(date.getMonth() + numOfMonths);

        return date;
    }
    setRealInterestRate(shopTerm, bankTerm) {
        if (this.checkEqualTerms()) {
            this.realInterestRate = this.fields.rate;
        } else {
            const ratelist = this.realInterestRateList;
            for (let i = 0; i < ratelist.length; i++) {
                if (
                    ratelist[i].payments_parts === shopTerm &&
                    ratelist[i].wish_payments_parts === bankTerm
                ) {
                    this.realInterestRate = ratelist[i].real_yearly_bet;
                }
            }
        }
    }

    calculateRealInterestRate() {
        /*  const realInterestRate = new RealInterestRate(
            this.requiredPayment,
            Number(this.getBankCreditTerm()),
            this.sumWithOutFirstPayment
        );
        this.realInterestRate = this.checkEqualTerms()
            ? this.fields.rate
            : realInterestRate.calculate();*/
        if (this.checkEqualTerms()) {
            this.realInterestRate = this.fields.rate;
        } else {
            const term = Number(this.getBankCreditTerm());
            // gets rate for more than 2 transactions
            const transactions = [
                {
                    amount: this.sumWithOutFirstPayment,
                    when: new Date()
                }
            ];
            for (let i = 1; i < term; i++) {
                transactions[i] = {
                    amount: -1 * this.requiredPayment,
                    when: this.addMonths(i)
                };
            }

            let rate = this.fields.rate;
            if (transactions.length > 2) {
                rate = xirr(transactions);
            }

            this.realInterestRate = Math.abs(rate);
        }
    }

    getRealInterestRate() {
        return this.checkEqualTerms() ? this.fields.rate : this.realInterestRate;
    }

    checkEqualTerms() {
        return this.getBankCreditTerm() === this.getCreditTerm();
    }

    checkMoreThanBase() {
        return this.getBankCreditTerm() > this.getCreditTerm();
    }
}
